import { definePage, defineResource } from '@restavo/crud';
import React from 'react';
import EmptyState from 'src/components/Base/EmptyState';
import InventoryCategoryForm from 'src/components/Inventory/InventoryCategoryForm';
import { InventoryItemStatusEnum } from 'src/utils/inventory';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import {
  tenantInventoryCategories,
  tenantInventoryCategoryCreate,
  tenantInventoryCategoryDelete,
  tenantInventoryCategoryUpdate,
} from 'src/services/apis/tenantRequests';
import { QUERIES } from 'src/utils/react-query';
import { splitTags } from 'src/utils/tags';

const inventoryCategories = defineResource({
  key: QUERIES.INVENTORY_CATEGORIES,
  // The hand-written page fetched page 1 with limit 1000 and rendered every row, so this
  // matches its reach exactly rather than quietly truncating to 20. Whether categories SHOULD
  // paginate is a product question, deliberately not answered by a migration PR.
  pageSize: 1000,
  api: {
    list: tenantInventoryCategories,
    create: tenantInventoryCategoryCreate,
    update: tenantInventoryCategoryUpdate,
    remove: tenantInventoryCategoryDelete,
  },
  search: 'server',
  searchFields: ['name', 'description'],
});

export const inventoryCategoriesPage = definePage(inventoryCategories, {
  titleKey: 'titles.inventoryCategoriesPage',
  descriptionKey: 'common.inventoryCategories',
  createLabelKey: 'actions.createNewInventoryCategory',
  updateLabelKey: 'actions.updateInventoryCategory',
  deleteHeadingKey: 'messages.areYouSureToDeleteThisInventoryCategory',
  columns: [
    // The column a user edits by name must survive every prefs reconciliation.
    { key: 'name', headerKey: 'common.name', sortable: true, alwaysVisible: true },
    { key: 'description', headerKey: 'common.description' },
    {
      key: 'status',
      headerKey: 'common.status',
      sortable: true,
      // Same copy and Carbon types as InventoryCategoryStatusTag had, as a display kind.
      display: {
        kind: 'tag',
        map: {
          [InventoryItemStatusEnum.ACTIVE]: { labelKey: 'inventoryItemStatuses.active', type: 'cyan' },
          [InventoryItemStatusEnum.ARCHIVED]: { labelKey: 'inventoryItemStatuses.archived', type: 'gray' },
        },
      },
    },
    // server sorts createdAt ASC, so a freshly saved category lands at the bottom; a visible
    // updatedAt would not make that clearer. Hidden by default, one toggle away.
    { key: 'updatedAt', headerKey: 'common.updated', display: { kind: 'date' }, defaultHidden: true, sortable: true },
  ],
  form: InventoryCategoryForm,
  emptyState: <EmptyState />,
  toPayload: (values: any, params) => ({
    ...values,
    restaurantId: params.restaurantId,
    tags: splitTags(values.tags),
  }),
  useTitleVars: () => ({ restaurantName: useCurrentRestaurant()?.name }),
});
